import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { TodayResponse, MealSlot, Dish } from '../types';

export function useToday(date: string) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['today', date],
    queryFn: () => api.get<TodayResponse>(`/today?date=${date}`),
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['today'] });
  }

  const chooseDishMutation = useMutation({
    mutationFn: ({ slot, dishId, source }: { slot: MealSlot; dishId: number; source: 'SUGGESTED' | 'SWAPPED' }) =>
      api.post('/logs', { date, slot, dishId, source }),
    onSuccess: invalidate,
  });
  const ateOutMutation = useMutation({
    mutationFn: ({ slot, note }: { slot: MealSlot; note?: string }) =>
      api.post('/logs/ate-out', { date, slot, note }),
    onSuccess: invalidate,
  });
  const undoLogMutation = useMutation({
    mutationFn: (logId: number) => api.del(`/logs/${logId}`),
    onSuccess: invalidate,
  });

  async function refreshSlot(slot: MealSlot, excludeIds: number[]): Promise<Dish[]> {
    const res = await api.get<{ slot: MealSlot; suggestions: Dish[] }>(
      `/today/${slot}/refresh?date=${date}&exclude=${excludeIds.join(',')}`,
    );
    return res.suggestions;
  }

  return {
    data: data ?? null,
    loading: isLoading,
    chooseDish: async (slot: MealSlot, dishId: number, source: 'SUGGESTED' | 'SWAPPED' = 'SUGGESTED') => {
      await chooseDishMutation.mutateAsync({ slot, dishId, source });
    },
    ateOut: async (slot: MealSlot, note?: string) => {
      await ateOutMutation.mutateAsync({ slot, note });
    },
    undoLog: async (logId: number) => {
      await undoLogMutation.mutateAsync(logId);
    },
    refreshSlot,
    reload: () => queryClient.invalidateQueries({ queryKey: ['today', date] }),
  };
}
